import type { ObraDetail } from '@obras/shared';
import { Crown, Trash2, UserCog, UserPlus } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Card, CardHeader } from '../../components/ui/Card.js';
import { Avatar, EmptyState, FormError } from '../../components/ui/Feedback.js';
import { MultiSelect, type MultiSelectOption } from '../../components/ui/MultiSelect.js';
import { Modal } from '../../components/ui/Modal.js';
import { ApiError } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { useOrgMembers } from '../usuarios/api.js';
import { useAssignFiscal, useRemoveFiscal } from './api.js';

const ICON = { strokeWidth: 1.5 } as const;

/**
 * Quem trabalha nesta obra.
 *
 * Duas portas de entrada. Quem gere a equipe designa pela tela, e pode designar
 * quantos quiser; e quem publica relatório ou anexa arquivo entra sozinho como
 * auxiliar — antes disso a lista mentia, porque alguém com seis vistorias feitas
 * não aparecia em lugar nenhum.
 *
 * **Responsável é vaga única.** Promover alguém rebaixa quem estava, porque o
 * prazo de visita e a cobrança apontam para uma pessoa, e duas seria o mesmo
 * que nenhuma.
 */
export function ObraTeamTab({ obra }: { obra: ObraDetail }) {
  const { user, may } = useAuth();
  const membros = useOrgMembers();
  const designar = useAssignFiscal(obra.id);
  const remover = useRemoveFiscal(obra.id);

  const [incluindo, setIncluindo] = useState(false);
  const [escolhidos, setEscolhidos] = useState<string[]>([]);
  const [erro, setErro] = useState<string | null>(null);

  /*
   * Quem mexe na equipe desta obra (ADR 045).
   *
   * Gestor para cima e quem tem a permissão alcançam a equipe de qualquer obra,
   * inclusive a vaga de responsável. Quem responde pela obra, ou a cadastrou,
   * inclui e retira auxiliares — é a própria equipe — mas não transfere a
   * titularidade, que é decisão de gestão. O servidor aplica a mesma regra.
   */
  const gereQualquerEquipe = may('canManageObraTeam');
  const daObra =
    user !== null && (user.id === obra.responsibleFiscal?.id || user.id === obra.createdById);
  const podeGerir = gereQualquerEquipe || daObra;
  const podeDesignarResponsavel = gereQualquerEquipe;
  const naEquipe = new Set(obra.fiscais.map((fiscal) => fiscal.id));

  const opcoes: MultiSelectOption[] = (membros.data?.items ?? [])
    .filter((membro) => !naEquipe.has(membro.id))
    .map((membro) => ({
      value: membro.id,
      label: membro.name,
      ...(membro.jobTitle ? { detail: membro.jobTitle } : {}),
    }));

  async function incluir(): Promise<void> {
    setErro(null);

    try {
      // Um por vez: a rota designa uma pessoa, e assim um erro no meio diz qual.
      for (const userId of escolhidos) {
        await designar.mutateAsync({ userId, role: 'auxiliar' });
      }
      setEscolhidos([]);
      setIncluindo(false);
    } catch (falha) {
      setErro(falha instanceof ApiError ? falha.message : 'Não foi possível incluir na equipe.');
    }
  }

  const incluirBotao = podeGerir ? (
    <Button
      variant="primary"
      icon={<UserPlus {...ICON} size={16} />}
      onClick={() => setIncluindo(true)}
    >
      Incluir na equipe
    </Button>
  ) : null;

  return (
    <div className="flex flex-col gap-4">
      <Card square>
        <CardHeader
          title="Equipe da obra"
          description="Quem responde pela obra e quem trabalha nela."
          {...(obra.fiscais.length > 0 && incluirBotao ? { action: incluirBotao } : {})}
        />

        {obra.fiscais.length === 0 ? (
          <EmptyState
            icon={UserCog}
            title="Ninguém na equipe"
            description="Inclua os servidores que acompanham esta obra. Quem publicar relatório ou anexar arquivo passa a integrar a equipe automaticamente."
            {...(incluirBotao ? { action: incluirBotao } : {})}
          />
        ) : (
          <ul className="divide-y divide-line">
            {obra.fiscais.map((fiscal) => {
              const responsavel = fiscal.role === 'responsavel';

              return (
                <li key={fiscal.id} className="flex items-center gap-3 px-5 py-3">
                  <Avatar name={fiscal.name} url={fiscal.avatarUrl} size={30} />
                  <span className="min-w-0 flex-1 truncate text-[14px] font-medium">
                    {fiscal.name}
                  </span>

                  <Badge tone={responsavel ? 'primary' : 'muted'}>
                    {responsavel ? 'Responsável' : 'Auxiliar'}
                  </Badge>

                  {podeGerir && !responsavel ? (
                    <>
                      {podeDesignarResponsavel ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          icon={<Crown {...ICON} size={14} />}
                          loading={designar.isPending}
                          onClick={() =>
                            designar.mutate({ userId: fiscal.id, role: 'responsavel' })
                          }
                        >
                          Tornar responsável
                        </Button>
                      ) : null}

                      <Button
                        size="icon"
                        variant="ghost"
                        className="size-9"
                        title={`Tirar ${fiscal.name} da equipe`}
                        aria-label={`Tirar ${fiscal.name} da equipe`}
                        loading={remover.isPending}
                        onClick={() => remover.mutate(fiscal.id)}
                      >
                        <Trash2 {...ICON} size={16} aria-hidden />
                      </Button>
                    </>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <Modal
        open={incluindo}
        onClose={() => {
          setIncluindo(false);
          setEscolhidos([]);
          setErro(null);
        }}
        title="Incluir na equipe"
        description={
          podeDesignarResponsavel
            ? 'Entram como auxiliares. Depois, qualquer um deles pode ser promovido a responsável.'
            : 'Entram como auxiliares. A designação do responsável compete à gestão.'
        }
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setIncluindo(false);
                setEscolhidos([]);
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              loading={designar.isPending}
              disabled={escolhidos.length === 0}
              icon={<UserPlus {...ICON} size={16} />}
              onClick={() => void incluir()}
            >
              {escolhidos.length <= 1 ? 'Incluir' : `Incluir ${escolhidos.length}`}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <FormError>{erro}</FormError>

          <MultiSelect
            label="Pessoas"
            variant="field"
            searchable
            options={opcoes}
            selected={escolhidos}
            onChange={setEscolhidos}
          />

          {opcoes.length === 0 ? (
            <p className="text-[13px] text-ink-3">
              Todos os usuários do órgão já integram a equipe desta obra.
            </p>
          ) : null}
        </div>
      </Modal>
    </div>
  );
}
